import {Tooltip, Typography, TableSortLabel} from '@material-ui/core';
import React, {useContext} from 'react';
import getFilterComponent, {FilterProperties} from 'Shared/components/PagedView/Filter';
import 'Shared/components/PagedView/Table/ColumnHeader.sass';
import TableFilterMenu from 'Shared/components/PagedView/Table/TableFilterMenu';
import Draggable from 'react-draggable';
import {ColumnWidthsContext} from 'Shared/components/PagedView/Table/Table';
import {DragIndicator} from '@material-ui/icons';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

export type SharedColumnHeaderProps<TFilter, TData> = {
  title: string;
  sortColumnKey?: keyof TData;
  filterProperties?: FilterProperties<TFilter>;
  titleOrType?: string;
  idx?: number;
};

export type ColumnHeaderProps<TFilter, TData> = {
  onColumnResize: (columnKey: string, newSize: number) => void;
  onChangeSort: ((sort?: Sort) => void) | undefined;
  sort: Sort | undefined;
  isLastColumn: boolean;
} & SharedColumnHeaderProps<TFilter, TData>;

export const getNextSortState = (sort?: Sort) => (columnName: string): Sort | undefined =>
  !sort
    ? {direction: 'asc', column: columnName}
    : sort.direction === 'asc'
    ? {direction: 'desc', column: columnName}
    : undefined;

    const getAriaSortAttribute = (sort?: Sort, sortColumnKey?: string) => {
      const appliedSortOrder = sort?.direction === 'asc' ? 'ascending' : 'descending';
      return sort && sort.column === sortColumnKey ? appliedSortOrder : 'none';
    };

const ColumnHeader = <TFilter, TData>(props: ColumnHeaderProps<TFilter, TData>) => {
  const {onColumnResize, sort, title, sortColumnKey, isLastColumn, filterProperties, onChangeSort, titleOrType, idx} = props;
  const columnWidths = useContext(ColumnWidthsContext);
  const FilterComponent = filterProperties ? getFilterComponent(title, filterProperties) : null;

  return (
    <div
      id={`column-header-${title}`}
      key={`column-header__${title}`}
      className="paged-table__cell"
      style={{width: `${columnWidths.get(title)}%`}}
    >
      <div className="column-header__table-cell-content">
        <div className="column-header__titles">
          <Tooltip title={title}>
            {onChangeSort && sortColumnKey ? (
              <TableSortLabel
                className="column-header__sort-label"
                active={!!sort && sort.column === sortColumnKey}
                // @ts-ignore
                direction={sort && sort.direction}
                role="columnheader"
                aria-label={title}
                aria-colindex={idx === undefined ? 0 : idx + 1}
                aria-sort={getAriaSortAttribute(sort, String(sortColumnKey))}
                onClick={() => {
                  const columnAlreadyHasSort = sort?.column === sortColumnKey;

                  if (columnAlreadyHasSort) {
                    onChangeSort(getNextSortState(sort)(sortColumnKey as string));
                  } else {
                    onChangeSort({direction: 'asc', column: sortColumnKey as string});
                  }
                }}
              >
                <Typography id={title ? `column-header-${toKebabCase(title)}` : `column-header-${idx}`} className="column-header__label" aria-label={title}>{title}</Typography>
              </TableSortLabel>
            ) : (
              <Typography id={title ? `column-header-${toKebabCase(title)}` : `column-header-${idx}`} className="column-header__label" role="columnheader" aria-label={title}>{title}</Typography>
            )}
          </Tooltip>

          {FilterComponent && filterProperties && (
            <TableFilterMenu
              title={title}
              filterProperties={filterProperties! as any}
              FilterComponent={FilterComponent}
              titleOrType={titleOrType}
            />
          )}
        </div>
        {!isLastColumn && (
          <div>
            {/* @ts-ignore */}
            <Draggable
              axis="x"
              defaultClassName="paged-table__change-column-size-icon"
              defaultClassNameDragging="paged-table__change-column-size-icon--active"
              onDrag={(event, {deltaX}) => {
                onColumnResize(title, deltaX);
              }}
              position={{x: 0, y: 0}}
            >
              <span>
                <div id={`column-header-resize-${toKebabCase(title)}`} className="column-header__resize-indicator" aria-hidden="true">
                  <DragIndicator />
                </div>
              </span>
            </Draggable>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnHeader;
